/**
 * Codex Design System — Motion & Animation
 *
 * All values from Figma Foundations §8.
 * Motion should feel natural and supportive, never distracting.
 *
 * Principles:
 *   Purposeful — every animation serves a function
 *   Brief      — if it's noticeable, it's too long
 *   Accessible — always respect prefers-reduced-motion
 *   Natural    — decelerate (ease-out) entering, accelerate (ease-in) exiting
 */

// ---------------------------------------------------------------------------
// Duration scale (ms)
// ---------------------------------------------------------------------------

export const durations = {
  shortest: 150, // micro-interactions: hover, focus ring, opacity change
  shorter: 200, // button press, icon rotation, toggle switch
  short: 250, // tooltip show/hide, chip animate, badge appear
  entering: 225, // elements entering the viewport (decelerate)
  leaving: 195, // elements exiting the viewport (accelerate)
  standard: 300, // card transitions, panel slide, tab switch
  complex: 375, // page transitions, drawer open/close, modal enter
} as const;

export type DurationKey = keyof typeof durations;

// ---------------------------------------------------------------------------
// Easing curves (cubic-bezier)
// ---------------------------------------------------------------------------

export const easings = {
  /** Standard — repositioning, resizing, state changes */
  easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
  /** Decelerate — elements entering, expanding, appearing */
  easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
  /** Accelerate — elements exiting, collapsing, disappearing */
  easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
  /** Sharp — quick directional changes */
  sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
  /** Linear — progress bars, loading indicators */
  linear: 'cubic-bezier(0, 0, 1, 1)',
  /** Spring / bounce — playful interactions (like button, celebration) */
  spring: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
} as const;

export type EasingKey = keyof typeof easings;
